import React from 'react'
import { Modal, Button, Popconfirm, Tag } from 'antd'
import styled from 'styled-components'
import { stripTransientProps } from 'src/utils/stripTransientProps'
import {
  formatInvoiceTotalDisplay,
  formatSupplierTitleDisplay,
} from 'src/utils/invoiceUploadHelpers'
import { canShowMatchAction } from 'src/utils/invoiceMatchActionHelpers'
import {
  getChooserInvoiceStatusLabel,
  getGoodsReceiptingInvoiceAmbientLines,
} from 'src/utils/goodsReceiptingMatchHelpers'
import { getRemoveFromPoActionMeta } from 'src/utils/invoiceRemoveFromPoHelpers'

/**
 * Pick which invoice to Match when more than one invoice touches this PO,
 * or when a leftover open invoice sits beside an already-reconciled one.
 *
 * Separate invoice rows = separate documents. “N pages” on a row means one
 * multi-page invoice (not multiple invoices).
 */
const GrMatchInvoiceChooserModal = ({
  visible,
  order,
  invoices = [],
  focusInvoiceId = null,
  onSelect,
  onRemove,
  onCancel,
  removingInvoiceId = null,
}) => {
  if (!order) return null

  const hasClosed = invoices.some(
    invoice =>
      invoice?.status === 'reconciled' || invoice?.status === 'sentToXero',
  )
  const hasOpen = invoices.some(invoice => canShowMatchAction(invoice))
  const po = String(order.purchaseOrderNumber || '').trim()

  return (
    <StyledModal
      title='Invoices on this purchase order'
      visible={visible}
      onCancel={onCancel}
      footer={<Button onClick={onCancel}>Cancel</Button>}
      destroyOnClose
      width={560}
    >
      <Lead>
        {hasClosed && hasOpen ? (
          <>
            One or more invoices are already reconciled on{' '}
            <strong>{order.purchaseOrderNumber || 'this order'}</strong>, but
            another still keeps Match open. Each row is a separate invoice —
            Match the correct leftover, or Remove from this PO if it does not
            belong here.
          </>
        ) : (
          <>
            More than one invoice relates to{' '}
            <strong>{order.purchaseOrderNumber || 'this order'}</strong>. Each
            row is a separate invoice (pages on one invoice stay on one row).
            Choose which to Match (same flow as Invoices), or Remove a wrong PO
            assignment.
          </>
        )}
      </Lead>
      <List>
        {invoices.map(invoice => {
          const id = invoice.id || invoice._id
          const canMatch = canShowMatchAction(invoice)
          const removeMeta = getRemoveFromPoActionMeta(invoice)
          const statusLabel = getChooserInvoiceStatusLabel(invoice)
          const ambient = getGoodsReceiptingInvoiceAmbientLines(invoice, po)
          const isRemoving = removingInvoiceId === id
          const isClosed =
            invoice?.status === 'reconciled' || invoice?.status === 'sentToXero'
          const isFocused =
            focusInvoiceId != null && String(focusInvoiceId) === String(id)

          const detailParts = [formatInvoiceTotalDisplay(invoice.invoiceTotal)]
          if (ambient.pageLabel) detailParts.push(ambient.pageLabel)
          if (ambient.sliceLabel)
            detailParts.push(`This PO ${ambient.sliceLabel}`)
          if (ambient.remainingLabel) detailParts.push(ambient.remainingLabel)

          return (
            <Row key={id} $muted={isClosed} $focused={isFocused}>
              <Meta>
                <TitleRow>
                  <strong>
                    {invoice.invoiceNumber || 'Invoice'} ·{' '}
                    {formatSupplierTitleDisplay(invoice.supplierTitle)}
                  </strong>
                  <StatusTag $closed={isClosed}>{statusLabel}</StatusTag>
                </TitleRow>
                <span>{detailParts.filter(Boolean).join(' · ')}</span>
              </Meta>
              <Actions>
                {removeMeta.canShow ? (
                  <Popconfirm
                    title={removeMeta.confirmCopy}
                    placement='topRight'
                    overlayStyle={{ maxWidth: 360 }}
                    onConfirm={() => onRemove?.(invoice)}
                    okText='Remove'
                    cancelText='Cancel'
                    disabled={Boolean(removingInvoiceId)}
                  >
                    <Button
                      disabled={Boolean(removingInvoiceId)}
                      loading={isRemoving}
                    >
                      {removeMeta.buttonLabel}
                    </Button>
                  </Popconfirm>
                ) : null}
                {canMatch ? (
                  <Button
                    type='primary'
                    disabled={Boolean(removingInvoiceId)}
                    onClick={() => onSelect?.(invoice)}
                  >
                    Match
                  </Button>
                ) : null}
              </Actions>
            </Row>
          )
        })}
      </List>
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
  }
`

const Lead = styled.p`
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.65);
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid
    ${props =>
      props.$focused ? '#91d5ff' : props.$muted ? '#f0f0f0' : '#d9d9d9'};
  border-radius: 8px;
  background: ${props =>
    props.$focused ? '#e6f7ff' : props.$muted ? '#fafafa' : '#fff'};
  box-shadow: ${props =>
    props.$focused ? '0 0 0 1px rgba(24, 144, 255, 0.2)' : 'none'};
`

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  min-width: 0;

  span {
    color: rgba(0, 0, 0, 0.45);
  }
`

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`

const StatusTag = styled(stripTransientProps(Tag))`
  && {
    margin: 0;
    font-size: 11px;
    line-height: 18px;
    color: ${props => (props.$closed ? '#389e0d' : '#873800')};
    background: ${props => (props.$closed ? '#f6ffed' : '#fff7e6')};
    border-color: ${props => (props.$closed ? '#b7eb8f' : '#ffd591')};
  }
`

const Actions = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
`

export default GrMatchInvoiceChooserModal
